import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { User, Mail, Phone, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Register = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref') || '';
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [profileExists, setProfileExists] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  // Persist the referral code so it survives a refresh if the user came via /register?ref=XYZ.
  useEffect(() => {
    if (referralCode) {
      try { localStorage.setItem('referral_code', referralCode); } catch (_) {}
    }
  }, [referralCode]);
  const effectiveReferralCode = referralCode || (typeof window !== 'undefined' && localStorage.getItem('referral_code')) || '';

  const content = {
    en: {
      title: "Create Your Profile",
      subtitle: "Join TALITHA KOUMI TUG and register for our upcoming conference",
      firstName: "First Name",
      lastName: "Last Name",
      email: "Email Address",
      phone: "Phone Number",
      password: "Password",
      confirmPassword: "Confirm Password",
      submit: "Create Profile",
      hasAccount: "Already have an account?",
      login: "Sign In",
      passwordMismatch: "Passwords do not match",
      success: "Profile created successfully! Please sign in.",
      error: "An error occurred. Please try again.",
      loading: "Loading...",
      profileExistsTitle: "You already have a profile",
      profileExistsBody: "This email is already registered. Please sign in to register for the event.",
      signInCta: "Sign In to Register",
    },
    fr: {
      title: "Créer Votre Profil",
      subtitle: "Rejoignez TALITHA KOUMI TUG et inscrivez-vous à notre prochaine conférence",
      firstName: "Prénom",
      lastName: "Nom",
      email: "Adresse courriel",
      phone: "Numéro de Téléphone",
      password: "Mot de Passe",
      confirmPassword: "Confirmer le Mot de Passe",
      submit: "Créer le Profil",
      hasAccount: "Vous avez déjà un compte?",
      login: "Se connecter",
      passwordMismatch: "Les mots de passe ne correspondent pas",
      success: "Profil créé avec succès! Veuillez vous connecter.",
      error: "Une erreur s'est produite. Veuillez réessayer.",
      loading: "Chargement...",
      profileExistsTitle: "Vous avez déjà un profil",
      profileExistsBody: "Ce courriel est déjà enregistré. Veuillez vous connecter pour vous inscrire à l'événement.",
      signInCta: "Se connecter pour s'inscrire",
    }
  };

  const t = content[language] || content.fr;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setProfileExists(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError(t.passwordMismatch);
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/register`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        referral_code: effectiveReferralCode || undefined,
      });
      // Clear the stored referral code after successful registration.
      try { localStorage.removeItem('referral_code'); } catch (_) {}
      navigate('/login', { state: { message: t.success } });
    } catch (err) {
      if (err.response?.status === 409) {
        setProfileExists(true);
        setError('');
      } else {
        setError(err.response?.data?.detail || t.error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-[#D4AF37] py-4">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_talitha-koumi/artifacts/xsd39lyf_IMG-20260121-WA0012.jpg"
              alt="TALITHA KOUMI TUG"
              className="h-12 w-auto"
            />
          </Link>
          <Link to="/login" className="text-white hover:underline font-medium">
            {t.login}
          </Link>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full">
          <h1 className="text-2xl font-bold text-[#1D1D1D] mb-2">{t.title}</h1>
          <p className="text-gray-600 mb-6">{t.subtitle}</p>

          {effectiveReferralCode && (
            <div data-testid="referral-banner" className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5 text-sm text-amber-800 flex items-center gap-2">
              <span className="font-bold">{language === 'fr' ? 'Code de partenaire :' : 'Partner code:'}</span>
              <code className="bg-white px-2 py-0.5 rounded font-mono">{effectiveReferralCode}</code>
              <span className="text-amber-700">{language === 'fr' ? '— vos inscriptions seront attribuées à ce partenaire.' : '— your sign-up will be attributed to this partner.'}</span>
            </div>
          )}

          {profileExists && (
            <div data-testid="profile-exists-message" className="bg-gradient-to-br from-amber-50 to-red-50 border border-amber-200 p-5 rounded-2xl mb-5">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <User className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <h3 className="font-bold text-[#1D1D1D] text-base mb-1">{t.profileExistsTitle}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{t.profileExistsBody}</p>
                </div>
              </div>
              <Link
                to={`/login?email=${encodeURIComponent(formData.email)}`}
                data-testid="profile-exists-signin-btn"
                className="w-full bg-[#CD0000] hover:bg-[#A00000] text-white font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 transition-colors text-sm"
              >
                {t.signInCta} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {error && (
            <div data-testid="register-error-message" className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="register-form" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.firstName}</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    data-testid="register-firstname-input"
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.lastName}</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    data-testid="register-lastname-input"
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.email}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="register-email-input"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.phone}</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="register-phone-input"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.password}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="register-password-input"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                />
                <button
                  type="button"
                  data-testid="register-toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.confirmPassword}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="register-confirm-password-input"
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  minLength={6}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                />
              </div>
            </div>

            <button
              type="submit"
              data-testid="register-submit-button"
              disabled={loading}
              className="w-full bg-[#CD0000] text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-[#c91560] transition-colors disabled:opacity-50"
            >
              {loading ? t.loading : t.submit} <ArrowRight className="w-5 h-5" />
            </button>
          </form>

          <p className="text-center mt-6 text-gray-600">
            {t.hasAccount}{' '}
            <Link to="/login" className="text-[#CD0000] font-medium hover:underline">
              {t.login}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
